import React, { useEffect, useState } from 'react';
import { Countdown } from 'components/Countdown/Countdown';
import './EggMissions.scss';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { useCountdown } from '../../hooks/useCountdown';
import { completeMission, getMissionsList } from 'utils/PointsAPI';
import { Mission, MissionCard } from '../Card/MissionCard';
import cls from 'classnames';
import { PlayToMintPlatformDisclaimer } from '../PlayToMint/PlayToMint';
import { useLoginRedirect } from 'hooks/useLoginRedirect';
import { useUser } from '../../hooks/useUser';
import { queryClient, queryKeys } from '../../contexts/QueryClient';
import { Route, routes } from '../../routing/routes';
import partition from 'lodash/partition';
import { Egg } from './Egg';

const dateCopy = new Date();
const nextMonday = new Date(
  dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7 || 7)),
).getTime();

function CountdownWrapper() {
  const countdown = useCountdown(nextMonday);
  return <Countdown countdown={countdown} />;
}

type MissionsListProps = {
  missions: Mission[];
  userId: string;
  setWeeklyMissions?: React.Dispatch<React.SetStateAction<Mission[]>>;
};

function MissionResetCountdown() {
  const missionResetCountdown = useCountdown(getMissionResetTime().getTime());
  return <Countdown countdown={missionResetCountdown} concise />;
}

function getMissionResetTime() {
  const currentDate = new Date();
  const flipDay = currentDate.getUTCHours() >= 5 ? 24 : 0;
  currentDate.setUTCHours(5 + flipDay, 0, 0, 0);
  return currentDate;
}
const MissionsList = ({ missions, userId, setWeeklyMissions }: MissionsListProps) => {
  const clickMission = async (url: Route, slug: string, type: string) => {
    if (type === 'weekly' && setWeeklyMissions) {
      await completeMission(userId, slug);
      setWeeklyMissions((missions) => {
        const newMissions = [...missions];
        const missionIndex = newMissions.findIndex((mission) => mission.slug === slug);
        newMissions[missionIndex].completed = true;
        return newMissions;
      });
    }

    // clear all user points cache after a point gain
    await queryClient.invalidateQueries(queryKeys.allUserPoints());
    window.open(url, '_blank');
  };

  return (
    <>
      {missions.map((mission) => {
        return (
          <MissionCard
            key={mission.id}
            onClickMission={(mission) =>
              clickMission(mission.url as Route, mission.slug, mission.type)
            }
            mission={mission}
          />
        );
      })}
    </>
  );
};

export const EggMissions = () => {
  const [primaryMissions, setPrimaryMissions] = useState<Mission[]>([]);
  const [weeklyMissions, setWeeklyMissions] = useState<Mission[]>([]);
  const { account } = useUser();
  useLoginRedirect({ follow: routes.missions() });

  useEffect(() => {
    if (account?.dusktech?.userId) {
      getMissionsList(account?.dusktech?.userId).then((data) => {
        const weeklyMissions = data.filter((mission) => mission.type === 'weekly');
        const primaryMissions = data.filter((mission) => mission.type === 'primary');

        setWeeklyMissions(weeklyMissions);
        setPrimaryMissions(primaryMissions);
      });
    }
  }, [account.dusktech?.userId]);

  const [twilightShiftMission, otherPrimaryMissions] = partition(
    primaryMissions,
    (mission) => mission.slug === 'join-twilight-shift',
  );

  return (
    <Egg>
      <div>
        <div className="missions-title">
          <div>
            <h1>MISSIONS</h1>
            <p>Earn extra OmniGrow each week by exploring the world of DuskBreakers</p>
            <div className="play-to-mint-disclaimer-wrapper">
              <PlayToMintPlatformDisclaimer omnigrow />
            </div>
          </div>
          <HorizontalDivider className="d-md-none" />
        </div>
        {account.dusktech?.userId && (
          <>
            <h2>Primary</h2>
            <div className="missions">
              {twilightShiftMission[0] && otherPrimaryMissions.length > 0 && (
                <>
                  <div className="twilight-shift-mission">
                    <MissionCard
                      mission={twilightShiftMission[0]}
                      onClickMission={() => (window.location.href = twilightShiftMission[0].url)}
                    />
                    <div className="countdown">
                      <span className="resets-in">Resets In:</span>
                      <MissionResetCountdown />
                    </div>
                  </div>
                  <MissionsList missions={otherPrimaryMissions} userId={account.dusktech?.userId} />
                </>
              )}
            </div>
            <div
              className={cls('missions-weekly-header', {
                'missions-weekly-missing': weeklyMissions.length === 0,
              })}
            >
              {weeklyMissions.length > 0 && <h2>Weekly</h2>}
              <div className="countdown">
                <p>NEW MISSIONS IN</p>
                <CountdownWrapper />
              </div>
            </div>
            <div className="missions">
              {weeklyMissions && (
                <MissionsList
                  missions={weeklyMissions}
                  userId={account.dusktech?.userId}
                  setWeeklyMissions={setWeeklyMissions}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Egg>
  );
};
